import { makeAutoObservable, runInAction } from 'mobx'
import {
	getCounselorParentContacts,
	listParentContacts,
	createParentContact,
	updateParentContact,
	deleteParentContact,
} from 'api/parentCommunications'

const getApiErrorMessage = (error, fallback) =>
	error?.response?.data?.error || error?.message || fallback

/** Mirror backend single-primary rule in local store (no extra API calls). */
const applyLocalPrimary = (contacts, nextContact) => {
	if (!nextContact?.is_primary) return contacts
	return contacts.map(item =>
		item.id === nextContact.id ? item : { ...item, is_primary: false },
	)
}

class ParentContactsStore {
	/** @type {Map<string, Array>} */
	contactsByStudentId = new Map()
	/** @type {Map<string, string>} idle | loading | success | error */
	loadStatusByStudentId = new Map()
	/** idle | loading | success | error — counselor-wide bulk load */
	bulkLoadStatus = 'idle'
	error = null
	_inflightByStudentId = new Map()
	_bulkInflight = null

	constructor() {
		makeAutoObservable(
			this,
			{ _inflightByStudentId: false, _bulkInflight: false },
			{ autoBind: true },
		)
	}

	getContacts(studentId) {
		const id = String(studentId || '').trim()
		return this.contactsByStudentId.get(id) || []
	}

	getLoadStatus(studentId) {
		const id = String(studentId || '').trim()
		return this.loadStatusByStudentId.get(id) || 'idle'
	}

	setContactsForStudent(studentId, contacts) {
		const id = String(studentId || '').trim()
		if (!id) return
		this.contactsByStudentId.set(id, contacts || [])
		this.loadStatusByStudentId.set(id, 'success')
	}

	/**
	 * Apply a flat counselor-wide contacts list into contactsByStudentId.
	 * Students with no contacts get an empty array marked success.
	 */
	applyBulkContacts(contacts, studentIds = []) {
		const byStudent = new Map()
		for (const contact of contacts || []) {
			const sid = String(contact?.student_id || '').trim()
			if (!sid) continue
			if (!byStudent.has(sid)) byStudent.set(sid, [])
			byStudent.get(sid).push(contact)
		}

		const ids = new Set([
			...studentIds.map(id => String(id || '').trim()).filter(Boolean),
			...byStudent.keys(),
		])

		for (const id of ids) {
			this.contactsByStudentId.set(id, byStudent.get(id) || [])
			this.loadStatusByStudentId.set(id, 'success')
		}
	}

	/**
	 * One counselor-level GET for all parent contacts (no N+1).
	 */
	async loadAllContacts({ force = false, studentIds = [] } = {}) {
		if (!force && this.bulkLoadStatus === 'success' && !this._bulkInflight) {
			return this.contactsByStudentId
		}
		if (this._bulkInflight) return this._bulkInflight

		this.bulkLoadStatus = 'loading'
		this.error = null

		const promise = (async () => {
			try {
				const response = await getCounselorParentContacts()
				const contacts = Array.isArray(response?.data?.contacts)
					? response.data.contacts
					: []
				runInAction(() => {
					this.applyBulkContacts(contacts, studentIds)
					this.bulkLoadStatus = 'success'
					this.error = null
				})
				return this.contactsByStudentId
			} catch (error) {
				runInAction(() => {
					this.bulkLoadStatus = 'error'
					this.error = getApiErrorMessage(error, 'Failed to load parent contacts')
				})
				throw error
			} finally {
				this._bulkInflight = null
			}
		})()

		this._bulkInflight = promise
		return promise
	}

	async ensureContactsLoaded(studentId, { force = false } = {}) {
		const id = String(studentId || '').trim()
		if (!id) return []

		if (!force && this.loadStatusByStudentId.get(id) === 'success') {
			return this.getContacts(id)
		}

		const existing = this._inflightByStudentId.get(id)
		if (existing) return existing

		this.loadStatusByStudentId.set(id, 'loading')
		this.error = null

		const promise = (async () => {
			try {
				const response = await listParentContacts(id)
				const contacts = Array.isArray(response?.data) ? response.data : []
				runInAction(() => {
					this.contactsByStudentId.set(id, contacts)
					this.loadStatusByStudentId.set(id, 'success')
					this.error = null
				})
				return contacts
			} catch (error) {
				runInAction(() => {
					this.loadStatusByStudentId.set(id, 'error')
					this.error = getApiErrorMessage(error, 'Failed to load parent contacts')
				})
				throw error
			} finally {
				this._inflightByStudentId.delete(id)
			}
		})()

		this._inflightByStudentId.set(id, promise)
		return promise
	}

	invalidateStudent(studentId) {
		const id = String(studentId || '').trim()
		if (!id) return
		this.contactsByStudentId.delete(id)
		this.loadStatusByStudentId.delete(id)
		this._inflightByStudentId.delete(id)
	}

	invalidateAll() {
		this.contactsByStudentId = new Map()
		this.loadStatusByStudentId = new Map()
		this._inflightByStudentId = new Map()
		this.bulkLoadStatus = 'idle'
		this._bulkInflight = null
	}

	async createContact(studentId, body) {
		const id = String(studentId || '').trim()
		const response = await createParentContact(id, body)
		const contact = response?.data
		runInAction(() => {
			const current = this.getContacts(id)
			const next = contact ? [...current, contact] : current
			this.setContactsForStudent(id, applyLocalPrimary(next, contact))
		})
		return contact
	}

	async updateContact(studentId, contactId, body) {
		const id = String(studentId || '').trim()
		const response = await updateParentContact(id, contactId, body)
		const updated = response?.data
		runInAction(() => {
			const current = this.getContacts(id)
			const merged = current.map(item =>
				item.id === contactId ? { ...item, ...updated } : item,
			)
			this.setContactsForStudent(id, applyLocalPrimary(merged, updated))
		})
		return updated
	}

	async removeContact(studentId, contactId) {
		const id = String(studentId || '').trim()
		await deleteParentContact(id, contactId)
		runInAction(() => {
			const current = this.getContacts(id)
			this.setContactsForStudent(
				id,
				current.filter(item => item.id !== contactId),
			)
		})
	}
}

export const parentContactsStore = new ParentContactsStore()
export default parentContactsStore
